import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { usePageTitle } from "@/hooks/usePageTitle";
import { JobsActions } from "@/components/jobs/JobsActions";
import { JobsFilterWrapper } from "@/components/jobs/JobsFilterWrapper";
import { JobsContent } from "@/components/jobs/JobsContent";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { usePersistedViewMode } from "@/utils/viewMode";
import { JobsFilterProvider } from "@/contexts/JobsFilterContext";
import { useJobSkillsFilter } from "@/hooks/jobs/useJobSkillsFilter";
import { CandidatesViewOptions } from "@/components/candidates/CandidatesViewOptions";
import { DraggableKanbanBoard } from "@/components/jobs/kanban/DraggableKanbanBoard";
import { updateJob } from "@/utils/job/operations/updateJob";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useJobsFilter } from "@/contexts/JobsFilterContext";
import { useUserFavorites } from "@/hooks/favorites/useUserFavorites";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useJobsPaginated } from "@/hooks/jobs/useJobsPaginated";

// Job status configuration with colors
const JOB_STATUS_CONFIG = {
  'Open': {
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    headerBg: 'bg-green-100',
    headerText: 'text-green-800',
    dotColor: 'bg-green-500'
  },
  'Applications closed': {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    headerBg: 'bg-orange-100',
    headerText: 'text-orange-800',
    dotColor: 'bg-orange-500'
  },
  'On Hold': {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    headerBg: 'bg-yellow-100',
    headerText: 'text-yellow-800',
    dotColor: 'bg-yellow-500'
  },
  'Closed': {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    headerBg: 'bg-red-100',
    headerText: 'text-red-800',
    dotColor: 'bg-red-500'
  }
};

// Kanban stages for jobs by status
const JOB_STATUS_KANBAN_STAGES = [
  { id: 'open', name: 'Open' },
  { id: 'hold', name: 'On Hold' },
  { id: 'closed/won', name: 'Closed (Won)' },
  { id: 'closed/lost', name: 'Closed (Lost)' },
];

// Assigned to me filter component for the sort section
const AssignedToMeFilter = () => {
  const { t } = useTranslation();
  const { assignedToMe, setAssignedToMe } = useJobsFilter();

  return (
    <label className="flex items-center gap-2 cursor-pointer px-3 h-8 border border-input rounded-sm hover:bg-accent hover:text-accent-foreground text-xs">
      <Checkbox
        id="assigned-to-me"
        checked={assignedToMe}
        onCheckedChange={(checked) => setAssignedToMe(checked as boolean)}
        className="h-4 w-4"
      />
      <span className="text-sm">
        {t("jobs.assignedToMe", "Assigned to me")}
      </span>
    </label>
  );
};

// Component that uses the filter context - must be inside JobsFilterProvider
const JobOffersContent = ({
	skillsLoading,
	allSkills,
	jobStatuses,
	viewMode,
	setViewMode,
	isAddModalOpen,
	setIsAddModalOpen,
  handleJobDrop,
  handleRefresh,
  handleDeleteJobs,
  handleJobAdded,
  confirmDialog,
  setConfirmDialog,
  closingReason,
  setClosingReason,
  customReason,
  setCustomReason,
  handleConfirmClose
}: {
  skillsLoading: boolean;
  allSkills: any[];
  jobStatuses: string[];
  viewMode: any;
  setViewMode: (mode: any) => void;
  isAddModalOpen: boolean;
  setIsAddModalOpen: (open: boolean) => void;
  handleJobDrop: (jobId: string, newStatus: string) => void;
  handleRefresh: () => void;
  handleDeleteJobs: (jobIds: string[]) => Promise<void>;
  handleJobAdded: (job: any) => Promise<string>;
  confirmDialog: any;
  setConfirmDialog: (dialog: any) => void;
  closingReason: string;
  setClosingReason: (reason: string) => void;
  customReason: string;
  setCustomReason: (reason: string) => void;
  handleConfirmClose: () => void;
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Get sort controls from filter context - now inside the provider
  const { 
    sortField, 
    setSortField, 
    sortOrder, 
    setSortOrder,
    searchTerm,
    skillsFilter,
    skillsOperator,
    statusFilter,
    showClosed,
    maxDaysAgo,
    assignedToMe,
    showFavoritesOnly
  } = useJobsFilter();
  
  const { favorites } = useUserFavorites('job');
  
  // Use the paginated hook
  // In kanban view, always show closed jobs (they appear in the Closed column)
  const effectiveShowClosed = viewMode === 'kanban' ? true : showClosed;

  const {
    jobs,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    refetch
  } = useJobsPaginated(
    {
      searchTerm,
      skillsFilter,
      skillsOperator,
      statusFilter,
      showClosed: effectiveShowClosed,
      maxDaysAgo,
      assignedToMe,
      currentUserId: user?.id,
      showFavoritesOnly,
      favorites,
      disablePagination: viewMode === 'kanban'
    },
    sortField,
    sortOrder
  );
  
  usePageTitle("Jobs");

  return (
    <div className="w-full">
      <PageHeader 
        title={t("jobs.header", "Open Offers")}
        actions={
          <JobsActions 
            onAddClick={() => setIsAddModalOpen(true)} 
            onJobsGenerated={refetch}
          />
        }
      />
      
      <JobsFilterWrapper 
        allSkills={allSkills}
        allStatuses={jobStatuses}
      />
      
      <div className="mb-4">
        <CandidatesViewOptions
          sortField={sortField}
          setSortField={setSortField}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          viewMode={viewMode}
          setViewMode={setViewMode}
          allowedModes={["card", "list", "kanban"]}
          extra={
            <AssignedToMeFilter />
          }
        />
      </div>
      
      {viewMode === 'kanban' ? (
        <KanbanView jobs={jobs} onJobDrop={handleJobDrop} />
      ) : (
        <JobsContent
          jobs={jobs}
          isLoading={isLoading || skillsLoading}
          viewMode={viewMode as "card" | "list"}
          onRefresh={handleRefresh}
          onDeleteJobs={handleDeleteJobs}
        />
      )}
      
      <JobOffersAddModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddJob={handleJobAdded}
      />
      
      {/* Confirmation Dialog for Closing Jobs */}
      <AlertDialog open={confirmDialog.open} onOpenChange={open => setConfirmDialog(v => ({ ...v, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("jobs.closingReason.closeTitle", "Close Job Offer?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("jobs.closingReason.closeWarning", "Changing to Closed will close the job offer and all the linked matches. Are you sure you want to continue?")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">{t("jobs.closingReason.reasonLabel", "Reason for closing")}</label>
            <Select value={closingReason} onValueChange={setClosingReason}>
              <SelectTrigger>
                <SelectValue placeholder={t("jobs.closingReason.reasonPlaceholder", "Select reason")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Expired">{t("jobs.closingReason.expired", "Expired")}</SelectItem>
                <SelectItem value="Closed by customer">{t("jobs.closingReason.closedByCustomer", "Closed by customer")}</SelectItem>
                <SelectItem value="Candidate hired">{t("jobs.closingReason.candidateHired", "Candidate hired")}</SelectItem>
                <SelectItem value="Other">{t("jobs.closingReason.other", "Other")}</SelectItem>
              </SelectContent>
            </Select>
            {closingReason === "Other" && (
              <div className="mt-2">
                <Input
                  value={customReason}
                  onChange={e => setCustomReason(e.target.value)}
                  placeholder={t("jobs.closingReason.otherPlaceholder", "Specify reason")}
                />
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setConfirmDialog({ open: false });
              setClosingReason("");
              setCustomReason("");
            }}>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog.jobId && confirmDialog.newStatus) {
                  let reason = closingReason;
                  if (closingReason === "Other") reason = customReason;
                  handleConfirmClose();
                }
              }}
              disabled={!closingReason || (closingReason === "Other" && !customReason.trim())}
            >
              {t("jobs.closingReason.confirmClose", "Close Job")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const JobOffers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [viewMode, setViewMode] = usePersistedViewMode("jobs");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // State for confirmation dialog when changing to Closed
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean, jobId?: string, newStatus?: string }>({ open: false });
  const [closingReason, setClosingReason] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  
  const { skills: allSkills, isLoading: skillsLoading } = useJobSkillsFilter();

  // Create a memoized refresh function
  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleAddJob = async (jobData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();

      if (!userOrg) throw new Error("User organization not found");

      // Strip empty strings, null, undefined, and internal fields from the data
      const cleanedData = Object.fromEntries(
        Object.entries(jobData).filter(([k, v]) => {
          if (k.startsWith('_')) return false; // strip internal fields like _job_languages
          if (v === undefined || v === null) return false;
          if (typeof v === 'string' && v.trim() === '') return false;
          return true;
        })
      );

      const insertData = {
        ...cleanedData,
        organization_uuid: userOrg.organization_uuid,
        status: cleanedData.status || "open",
      };

      const { data, error} = await supabase
        .from('jobs')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Trigger AI analysis for the newly created job
      await supabase.functions.invoke(
        'ai-processing?command=push-new-analysis',
        {
          body: {
            entityId: data.id,
            entityType: "job",
            analysisType: "analyse-job-offer",
            actionOnComplete: "update-job",
          }
        }
      );

      toast({
        title: t("common.success"),
        description: t("jobs.addSuccess"),
      });

      setIsAddModalOpen(false);
      handleRefresh();
      
      // Navigate to the job detail page (client-side, keeps in-flight requests alive)
      navigate(`/jobs/${data.id}`);
      
      return data.id;
    } catch (error) {
      console.error("Error adding job:", error);
      toast({
        title: t("common.error"),
        description: error instanceof Error ? error.message : "Failed to add job",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteJobs = async (jobIds: string[]) => {
    try {
      const { error } = await supabase
        .from('jobs')
        .delete()
        .in('id', jobIds);

      if (error) throw error;

      toast({
        title: t("common.success"),
        description: t("common.deleteSuccess", { count: jobIds.length }),
      });

      handleRefresh();
    } catch (error) {
      console.error("Error deleting jobs:", error);
      toast({
        title: t("common.error"),
        description: t("common.deleteError"),
        variant: "destructive",
      });
    }
  };

  const handleJobDrop = async (jobId: string, newStatus: string) => {
    // Check if the job is being moved to "closed" status
    if (newStatus === "closed/won" || newStatus === "closed/lost") {
      setConfirmDialog({ open: true, jobId, newStatus });
      setClosingReason("");
      setCustomReason("");
      return;
    }
    
    // Proceed with the status update
    await updateJobStatus(jobId, newStatus);
  };

  const updateJobStatus = async (jobId: string, newStatus: string, closingReason?: string) => {
    try {
      const updateData: any = { status: newStatus };
      if (closingReason) {
        updateData.closing_reason = closingReason;
      }
      
      const success = await updateJob(jobId, updateData);
      if (success) {
        handleRefresh();
      }
    } catch (error) {
      console.error("Error updating job status:", error);
    }
  };
  
  // Static job statuses for filtering
  const jobStatuses = ['open', 'hold', 'closed/lost', 'closed/won'];

  return (
    <JobsFilterProvider viewMode={viewMode} setViewMode={setViewMode}>
      <JobOffersContent
        skillsLoading={skillsLoading}
        allSkills={allSkills}
        jobStatuses={jobStatuses}
        viewMode={viewMode}
        setViewMode={setViewMode}
        isAddModalOpen={isAddModalOpen}
        setIsAddModalOpen={setIsAddModalOpen}
        handleJobDrop={handleJobDrop}
        handleRefresh={handleRefresh}
        handleDeleteJobs={handleDeleteJobs}
        handleJobAdded={handleAddJob}
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        closingReason={closingReason}
        setClosingReason={setClosingReason}
        customReason={customReason}
        setCustomReason={setCustomReason}
        handleConfirmClose={() => {
          if (confirmDialog.jobId && confirmDialog.newStatus) {
            let reason = closingReason;
            if (closingReason === "Other") reason = customReason;
            updateJobStatus(confirmDialog.jobId, confirmDialog.newStatus, reason);
          }
          setConfirmDialog({ open: false });
          setClosingReason("");
          setCustomReason("");
        }}
      />
    </JobsFilterProvider>
  );
};

// Kanban view component
const KanbanView = ({ jobs, onJobDrop }: { jobs: any[], onJobDrop: (jobId: string, newStatus: string) => void }) => {
  const { t } = useTranslation();

  const handleJobDrop = (jobId: string, newStatus: string) => {
    onJobDrop(jobId, newStatus);
  };

  return (
    <DraggableKanbanBoard
      stages={JOB_STATUS_KANBAN_STAGES}
      candidatesByStage={JOB_STATUS_KANBAN_STAGES.reduce((acc, stage) => {
        const stageJobs = jobs.filter(j => (j.status || 'open') === stage.id);
        acc[stage.id] = stageJobs.map(j => ({
          id: j.id,
          name: j.title,
          company: j.companies?.name || j.company?.name || '',
          jobTitle: j.title,
          jobId: j.id,
          candidateId: '', // Leave empty to trigger job rendering
          stage: j.status || 'Open',
          createdAt: j.updated_at || j.created_at,
          applicationsCount: j.new_applications_count || 0,
          processesCount: 0,
          published: j.published || false,
        }));
        return acc;
      }, {} as Record<string, any[]>)}
      onCandidateDrop={handleJobDrop}
      statusConfig={JOB_STATUS_CONFIG}
    />
  );
};



export default JobOffers;
